import { useEffect, useMemo, useState } from "react";
import { http } from "@/lib/api";
import Layout from "@/components/Layout";
import { PageHero } from "@/hooks/usePageContent";
import ArtworkCard from "@/components/ArtworkCard";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "featured", label: "Featured" },
  { key: "digital", label: "Digital" },
  { key: "print", label: "Prints" },
  { key: "canvas", label: "Canvas" },
  { key: "members", label: "Members Only" },
  { key: "qr", label: "In Mobile Gallery" },
];

export default function Shop() {
  const [arts, setArts] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    http.get("/artworks").then(({ data }) => setArts(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    if (filter === "all") return arts;
    if (filter === "featured") return arts.filter(a => a.featured);
    if (filter === "qr") return arts.filter(a => a.qr_eligible);
    if (filter === "members") return arts.filter(a => a.subscriber_only);
    return arts.filter(a => (a.variants || []).some(v => v.type === filter && v.active));
  }, [arts, filter]);

  return (
    <Layout>
      <PageHero slug="shop" />
      <section className="bg-black pt-24 pb-12 border-b border-white/5">
        <div className="max-w-[1480px] mx-auto px-6 md:px-12">
          <h1 className="text-grunge text-5xl md:text-7xl text-[#F5F2EB] leading-[0.95] mb-6">
            EVERY PIECE,<br/>A REMINDER.
          </h1>
          <p className="font-body italic text-xl text-[#A3A3A3] max-w-2xl">
            Browse the growing collection. Each truth available in digital, print, or canvas — and each one ready to be displayed in town through the Mobile Gallery.
          </p>
        </div>
      </section>

      <section className="bg-black py-12">
        <div className="max-w-[1480px] mx-auto px-6 md:px-12">
          <div className="flex flex-wrap gap-2 mb-12 border-b border-white/10 pb-6" data-testid="shop-filters">
            {FILTERS.map(f => (
              <button
                key={f.key}
                data-testid={`filter-${f.key}`}
                onClick={() => setFilter(f.key)}
                className={`font-ui text-[11px] uppercase tracking-[0.22em] px-4 py-2 border transition-colors ${
                  filter === f.key
                    ? "bg-[#B8915A] text-black border-[#B8915A]"
                    : "bg-transparent text-[#F5F2EB] border-white/15 hover:border-[#B8915A] hover:text-[#B8915A]"
                }`}
              >
                {f.label}
              </button>
            ))}
            <div className="ml-auto font-ui text-[11px] tracking-[0.22em] text-[#737373] uppercase self-center">
              {filtered.length} {filtered.length === 1 ? "Piece" : "Pieces"}
            </div>
          </div>

          {loading ? (
            <div className="font-ui text-[#737373] text-sm tracking-widest uppercase">Loading the collection…</div>
          ) : filtered.length === 0 ? (
            <div className="font-body italic text-[#A3A3A3] text-lg">No pieces match this filter — yet. New truths land soon.</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8" data-testid="shop-grid">
              {filtered.map(a => <ArtworkCard art={a} key={a.id} />)}
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}
